import {ArrowLeft, ArrowRight, Close} from '@mui/icons-material';
import {IconButton, InputAdornment} from '@mui/material';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {DateTimePicker} from '@mui/x-date-pickers/DateTimePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {TimePicker} from '@mui/x-date-pickers/TimePicker';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import moment from 'moment-timezone';
import 'moment/locale/es';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {useFormContext, useWatch} from 'react-hook-form';
import FormErrors from 'src/view/shared/form/formErrors';
import {DisabledText} from './shared/formItemWrappers';

export function DatePickerFormItem(props) {
  const {
    label,
    name,
    hint,
    placeholder,
    autoFocus,
    autoComplete,
    externalErrorMessage,
    required,
    showTime,
    timeOnly,
    disabled,
    minDate,
    maxDate,
    resetValueFormItemName,
    style,
    defaultValue,
    views,
    preChangeFn,
    isEditing,
    monthNavigation,
    fullWidth,
    openTo,
  } = props;

  const parseDateValue = (value: any) => {
    if (!value) return null;
    if (timeOnly && typeof value === 'string') {
      const strictTime = moment(value, ['HH:mm', 'HH:mm:ss', 'H:mm', 'H:mm:ss'], true);
      if (strictTime.isValid()) return strictTime;
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed : null;
  };

  const {
    register,
    formState: {touchedFields, isSubmitted, errors},
    setValue,
    getValues,
    control,
  } = useFormContext();

  const ogValue = getValues(name);
  const defaultValueComp = ogValue || (!isEditing ? defaultValue || null : null);
  const defaultValueDate = parseDateValue(defaultValueComp);
  const watchName: any = useWatch({name, control, defaultValue: defaultValueDate});
  //console.log('%c⧭ DatePickerFormItem', 'color: #00e600', {name, watchName});

  useEffect(() => {
    register(name);
    if (defaultValueDate) setValue(name, defaultValueDate);
  }, []);

  const errorMessage = FormErrors.errorMessage(name, errors, touchedFields, isSubmitted, externalErrorMessage);
  const [pickerOpen, setPickerOpen] = useState(false);

  const DateTimePickerComponent = timeOnly ? TimePicker : showTime ? DateTimePicker : DatePicker;

  const format = props.format
    ? props.format
    : timeOnly
    ? 'HH:mm'
    : showTime
    ? 'DD-MM-YYYY HH:mm'
    : 'DD-MM-YYYY';
  const momentFormat = props.momentFormat ? props.momentFormat : format;

  let otherProps: any = {};
  if (timeOnly) {
    otherProps.ampm = false;
    otherProps.timeSteps = {minutes: 1};
  }

  if (disabled) {
    const parsedWatchValue = parseDateValue(watchName);
    const display = parsedWatchValue ? parsedWatchValue.format(momentFormat) : null;
    return <DisabledText label={label} value={display} />;
  }

  const handleChange = (value) => {
    if (preChangeFn) {
      value = preChangeFn(value);
    }
    setValue(name, value, {shouldValidate: true});
    if (resetValueFormItemName) {
      setValue(resetValueFormItemName, value, {
        shouldValidate: true,
      });
    }
    props.onChange && props.onChange(value);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DateTimePickerComponent
        readOnly={props.readOnly}
        open={pickerOpen}
        onOpen={() => setPickerOpen(true)}
        onClose={() => setPickerOpen(false)}
        slotProps={{
          textField: {
            fullWidth: fullWidth,
            InputLabelProps: {shrink: true},
            onKeyDown: (event) => {
              if (pickerOpen) return;
              if (event.key === 'ArrowDown' || event.key === 'Enter') {
                event.preventDefault();
                setPickerOpen(true);
              }
            },
            InputProps: {
              ...(monthNavigation
                ? {
                    startAdornment: (
                      <InputAdornment position="start" sx={{marginRight: 0}}>
                        <IconButton
                          disabled={!watchName}
                          style={{borderRadius: 50}}
                          onClick={($event) => {
                            $event.stopPropagation();
                            handleChange(moment(watchName).subtract(1, 'month'));
                          }}
                          // onMouseDown={() => handleChange(moment(watchName).subtract(1, 'month'))}
                          size="small">
                          <ArrowLeft fontSize="small" style={{color: 'gray'}} />
                        </IconButton>
                        <IconButton
                          disabled={!watchName}
                          style={{borderRadius: 50}}
                          onClick={($event) => {
                            $event.stopPropagation();
                            handleChange(moment(watchName).add(1, 'month'));
                          }}
                          // onMouseDown={() => handleChange(moment(watchName).add(1, 'month'))}
                          size="small">
                          <ArrowRight fontSize="small" style={{color: 'gray'}} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                : {}),
              ...(props.clearable && watchName
                ? {
                    endAdornment: (
                      <InputAdornment position="end" sx={{marginLeft: 0}}>
                        <IconButton
                          style={{borderRadius: 50}}
                          onClick={($event) => {
                            $event.stopPropagation();
                            handleChange(null);
                          }}
                          onMouseDown={() => handleChange(null)}
                          size="small">
                          <Close fontSize="small" style={{color: 'gray'}} />
                        </IconButton>
                      </InputAdornment>
                    ),
              }
            : {}),
            },
          },
          popper: {
            sx: {zIndex: 9999},
          },
          dialog: {
            sx: {zIndex: 9999},
          },
        }}
        className="MuiDatePickerFormItem"
        sx={{
          '& .MuiOutlinedInput-root': {
            padding: '8.5px 14px',
          },
          '& .MuiIconButton-root': {
            padding: '2px',
          },
          '& .MuiInputLabel-root': {
            top: '-6px',
          },
        }}
        style={style}
        views={views}
        openTo={openTo}
        minDate={minDate ? moment(minDate) : undefined}
        maxDate={maxDate ? moment(maxDate) : undefined}
        format={format}
        id={name}
        name={name}
        label={label}
        required={required}
        margin={props.margin || 'normal'}
        fullWidth
        inputVariant="outlined"
        size="small"
        onChange={handleChange}
        onBlur={(event) => {
          props.onBlur && props.onBlur(event);
        }}
        value={parseDateValue(watchName)}
        placeholder={placeholder || undefined}
        autoFocus={autoFocus || undefined}
        autoComplete={autoComplete || undefined}
        error={Boolean(errorMessage)}
        helperText={errorMessage || hint}
        autoOk
        {...otherProps}
      />
    </LocalizationProvider>
  );
}

DatePickerFormItem.defaultProps = {
  required: false,
};

DatePickerFormItem.propTypes = {
  name: PropTypes.string.isRequired,
  required: PropTypes.bool,
  label: PropTypes.string,
  hint: PropTypes.string,
  autoFocus: PropTypes.bool,
  size: PropTypes.string,
  prefix: PropTypes.string,
  placeholder: PropTypes.string,
  externalErrorMessage: PropTypes.string,
  showTime: PropTypes.bool,
  disabled: PropTypes.bool,
  minDate: PropTypes.any,
  maxDate: PropTypes.any,
  resetValueFormItemName: PropTypes.string,
  style: PropTypes.any,
  timeOnly: PropTypes.any,
  onChange: PropTypes.func,
  defaultValue: PropTypes.any,
  format: PropTypes.string,
  momentFormat: PropTypes.string,
  clearable: PropTypes.bool,
  views: PropTypes.array,
  openTo: PropTypes.string,
  preChangeFn: PropTypes.func,
  isEditing: PropTypes.bool,
  readOnly: PropTypes.bool,
  monthNavigation: PropTypes.bool,
  fullWidth: PropTypes.bool,
};

export default DatePickerFormItem;
